import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Event, EnterResultRequest } from '../../types/admin';
import { Player, Entry, Team, Pool, isRosterSelectionPicks } from '../../types/pool';
import { ExportPicksButton } from '../../components/ExportPicksButton';
import { calculateOffensiveFantasyPoints } from '../../utils/nflScoring';

export function AdminResults() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();
  const [pool, setPool] = useState<Pool | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedEventForTeams, setSelectedEventForTeams] = useState<Event | null>(null);
  const [showTeamsModal, setShowTeamsModal] = useState(false);
  // Track which event's championship toggle is mid-flight so we can show a spinner / disable.
  const [championshipBusy, setChampionshipBusy] = useState<string | null>(null);
  const [championshipMessage, setChampionshipMessage] = useState<{ eventId: string; text: string; tone: 'ok' | 'err' } | null>(null);

  useEffect(() => {
    loadData();
  }, [poolId]);

  /**
   * Flip an event's championship flag (2× scoring rule). After the backend
   * updates the flag and recalculates entry scores, refresh the page data so
   * the badge + leaderboard update.
   *
   * Confirms before turning OFF (since that DECREASES scores), not before
   * turning ON.
   */
  async function toggleChampionship(event: Event) {
    const turningOff = event.isFinal === true;
    if (turningOff) {
      const ok = window.confirm(
        `Remove championship flag from "${event.name}"?\n\nEntry scores will be RECALCULATED without the 2× multiplier for this game.`,
      );
      if (!ok) return;
    }
    try {
      setChampionshipBusy(event.id);
      setChampionshipMessage(null);
      const token = await getAccessTokenSilently();
      const res = await api.setEventChampionship(event.id, !turningOff, token);
      setChampionshipMessage({
        eventId: event.id,
        text: res.message + (res.entriesRecalculated > 0 ? ` (${res.entriesRecalculated} entries recalculated)` : ''),
        tone: 'ok',
      });
      await loadData();
    } catch (err: any) {
      setChampionshipMessage({
        eventId: event.id,
        text: err instanceof Error ? err.message : 'Failed to update championship flag',
        tone: 'err',
      });
    } finally {
      setChampionshipBusy(null);
    }
  }

  async function loadData() {
    if (!poolId) return;
    
    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const [poolData, eventsData, playersData, entriesData, teamsData] = await Promise.all([
        api.getPool(poolId),
        api.getPoolEvents(poolId, token),
        api.getPoolPlayers(poolId),
        api.getAdminPoolEntries(poolId, token),
        api.getPoolTeams(poolId),
      ]);
      setPool(poolData);
      setEvents(eventsData);
      setPlayers(playersData.players || []);
      setEntries(entriesData);
      setTeams(teamsData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  function openResultModal(event: Event) {
    setSelectedEvent(event);
    setShowResultModal(true);
  }

  function closeResultModal() {
    setSelectedEvent(null);
    setShowResultModal(false);
  }

  function openTeamsModal(event: Event) {
    setSelectedEventForTeams(event);
    setShowTeamsModal(true);
  }

  function closeTeamsModal() {
    setSelectedEventForTeams(null);
    setShowTeamsModal(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to="/admin"
          className="text-sm text-blue-600 hover:text-blue-700 mb-2 inline-block"
        >
          ← Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Enter Results</h1>
            {pool && (
              <p className="text-gray-600 mt-2">{pool.name}</p>
            )}
          </div>
          {pool && (
            <ExportPicksButton
              poolId={pool.id}
              poolName={pool.name}
              deadline={pool.deadline}
            />
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Events</h2>
        </div>
        <div className="divide-y divide-gray-200">
          {events.length === 0 ? (
            <div className="px-6 py-12 text-center text-gray-500">
              No events found. Import schedule data first.
            </div>
          ) : (
            events.map((event) => (
              <div key={event.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-medium text-gray-900">{event.name}</h3>
                      {event.isFinal && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                          Championship (2x)
                        </span>
                      )}
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        event.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        event.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {event.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                      <span>{new Date(event.scheduledAt).toLocaleString()}</span>
                      {event.round && (
                        <>
                          <span>•</span>
                          <span>{event.round}</span>
                        </>
                      )}
                      {event.location && (
                        <>
                          <span>•</span>
                          <span>{event.location}</span>
                        </>
                      )}
                    </div>
                    {event.homeTeam && event.awayTeam && (
                      <div className="mt-2 text-sm">
                        <span className="font-medium">{event.homeTeam.shortName}</span>
                        {event.homeScore !== undefined && event.awayScore !== undefined && (
                          <span className="mx-2 text-gray-600">
                            {event.homeScore} - {event.awayScore}
                          </span>
                        )}
                        {event.homeScore === undefined && <span className="mx-2 text-gray-400">vs</span>}
                        <span className="font-medium">{event.awayTeam.shortName}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    {/* Championship (2x) toggle — flips event.isFinal and triggers a recalculate.
                        Hidden for NFL No-Repeat, which has no double-point weeks. */}
                    {pool?.type !== 'NFL_NO_REPEAT' && (
                    <button
                      onClick={() => toggleChampionship(event)}
                      disabled={championshipBusy === event.id}
                      title={
                        event.isFinal
                          ? 'This game is flagged as the championship — all stats from it count as 2× per pool rules. Click to remove.'
                          : 'Mark this as the championship game (Stanley Cup Final / NBA Finals / Super Bowl / etc.) — all stats from it will count as 2×.'
                      }
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1 ${
                        event.isFinal
                          ? 'bg-yellow-100 hover:bg-yellow-200 text-yellow-900 border border-yellow-400'
                          : 'bg-white hover:bg-gray-50 text-gray-600 border border-gray-300'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <span aria-hidden="true">{event.isFinal ? '★' : '☆'}</span>
                      {championshipBusy === event.id
                        ? 'Saving…'
                        : event.isFinal
                        ? 'Championship (2×)'
                        : 'Mark as Championship'}
                    </button>
                    )}
                    {event.status === 'SCHEDULED' && (!event.homeTeam || !event.awayTeam) && (
                      <button
                        onClick={() => openTeamsModal(event)}
                        className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 rounded-lg transition-colors"
                      >
                        Set Teams
                      </button>
                    )}
                    <button
                      onClick={() => openResultModal(event)}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      {event.status === 'COMPLETED' ? 'Edit Result' : 'Enter Result'}
                    </button>
                  </div>
                </div>
                {championshipMessage && championshipMessage.eventId === event.id && (
                  <div
                    className={`mt-2 text-xs px-3 py-1.5 rounded ${
                      championshipMessage.tone === 'ok'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-rose-50 text-rose-800 border border-rose-200'
                    }`}
                  >
                    {championshipMessage.text}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {showResultModal && selectedEvent && (
        <ResultEntryModal
          event={selectedEvent}
          players={players}
          entries={entries}
          onClose={closeResultModal}
          onSuccess={() => {
            closeResultModal();
            loadData();
          }}
        />
      )}

      {showTeamsModal && selectedEventForTeams && (
        <SetTeamsModal
          event={selectedEventForTeams}
          teams={teams}
          onClose={closeTeamsModal}
          onSuccess={() => {
            closeTeamsModal();
            loadData();
          }}
        />
      )}
    </div>
  );
}

// Set Teams Modal Component
function SetTeamsModal({
  event,
  teams,
  onClose,
  onSuccess,
}: {
  event: Event;
  teams: Team[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [homeTeamId, setHomeTeamId] = useState(event.homeTeam?.id || '');
  const [awayTeamId, setAwayTeamId] = useState(event.awayTeam?.id || '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!homeTeamId || !awayTeamId) {
      setError('Please select both teams');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      
      await api.updateEventTeams(event.id, { homeTeamId, awayTeamId }, token);
      onSuccess();
    } catch (err) {
      console.error('Failed to set teams:', err);
      setError(err instanceof Error ? err.message : 'Failed to set teams');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Set Teams: {event.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#111827' }}>
                Home Team
              </label>
              <select
                value={homeTeamId}
                onChange={(e) => setHomeTeamId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                style={{ color: '#111827' }}
                required
              >
                <option value="">Select team...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#111827' }}>
                Away Team
              </label>
              <select
                value={awayTeamId}
                onChange={(e) => setAwayTeamId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                style={{ color: '#111827' }}
                required
              >
                <option value="">Select team...</option>
                {teams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
            >
              {loading ? 'Setting...' : 'Set Teams'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Scoring Calculator Component - Position Aware
function ScoringCalculator({ position, onApplyPoints }: { position: string; onApplyPoints: (points: number) => void }) {
  // Football offensive stats
  const [offensiveStats, setOffensiveStats] = useState({
    passingYards: '', passingTDs: '', interceptions: '',
    rushingYards: '', receivingYards: '',
    rushingTDs: '', receivingTDs: '',
    receptions: '', twoPointConversions: '', fumblesLost: '',
  });

  // Football kicking stats
  const [kickingStats, setKickingStats] = useState({
    fieldGoals30: '', fieldGoals40: '', fieldGoals50: '', fieldGoals50Plus: '', extraPoints: '',
  });

  // Football defense stats
  const [defenseStats, setDefenseStats] = useState({
    sacks: '', turnovers: '', touchdowns: '', safeties: '',
    blockedKickReturns: '', pointsAllowed: '',
  });

  // Hockey skater stats (F and D)
  const [skaterStats, setSkaterStats] = useState({
    goals: '', assists: '', plusMinus: '', powerPlayPoints: '',
    shotsOnGoal: '', blockedShots: '',
  });

  // Hockey goalie stats
  const [goalieStats, setGoalieStats] = useState({
    win: '', goalsAgainst: '', saves: '', shutout: '',
  });

  const isOffensive = ['QB', 'RB', 'WR', 'TE', 'FLEX'].includes(position);
  const isKicker = position === 'K';
  const isDefense = position === 'DEF';
  const isHockeySkater = position === 'F' || position === 'D';
  const isGoalie = position === 'G';

  const calculatePoints = () => {
    let total = 0;

    if (isHockeySkater) {
      // Hockey Skater Scoring: G=6, A=4, +/-=2, PPP=2, SOG=0.9, BLK=1
      total += (parseFloat(skaterStats.goals) || 0) * 6;
      total += (parseFloat(skaterStats.assists) || 0) * 4;
      total += (parseFloat(skaterStats.plusMinus) || 0) * 2;
      total += (parseFloat(skaterStats.powerPlayPoints) || 0) * 2;
      total += (parseFloat(skaterStats.shotsOnGoal) || 0) * 0.9;
      total += (parseFloat(skaterStats.blockedShots) || 0) * 1;
    } else if (isGoalie) {
      // Hockey Goalie Scoring: W=5, GA=-3, SV=0.6, SHO=5
      total += (parseFloat(goalieStats.win) || 0) * 5;
      total += (parseFloat(goalieStats.goalsAgainst) || 0) * -3;
      total += (parseFloat(goalieStats.saves) || 0) * 0.6;
      total += (parseFloat(goalieStats.shutout) || 0) * 5;
    } else if (isOffensive) {
      // Full-PPR offensive scoring (single source of truth in utils/nflScoring).
      total += calculateOffensiveFantasyPoints({
        passingYards: parseFloat(offensiveStats.passingYards) || 0,
        passingTDs: parseFloat(offensiveStats.passingTDs) || 0,
        interceptions: parseFloat(offensiveStats.interceptions) || 0,
        rushingYards: parseFloat(offensiveStats.rushingYards) || 0,
        receivingYards: parseFloat(offensiveStats.receivingYards) || 0,
        rushingTDs: parseFloat(offensiveStats.rushingTDs) || 0,
        receivingTDs: parseFloat(offensiveStats.receivingTDs) || 0,
        receptions: parseFloat(offensiveStats.receptions) || 0,
        twoPointConversions: parseFloat(offensiveStats.twoPointConversions) || 0,
        fumblesLost: parseFloat(offensiveStats.fumblesLost) || 0,
      }, position);
    } else if (isKicker) {
      total += (parseFloat(kickingStats.fieldGoals30) || 0) * 3;
      total += (parseFloat(kickingStats.fieldGoals40) || 0) * 3.5;
      total += (parseFloat(kickingStats.fieldGoals50) || 0) * 4.5;
      total += (parseFloat(kickingStats.fieldGoals50Plus) || 0) * 5.5;
      total += (parseFloat(kickingStats.extraPoints) || 0);
    } else if (isDefense) {
      total += (parseFloat(defenseStats.sacks) || 0);
      total += (parseFloat(defenseStats.turnovers) || 0) * 2;
      total += (parseFloat(defenseStats.touchdowns) || 0) * 6;
      total += (parseFloat(defenseStats.safeties) || 0) * 5;
      total += (parseFloat(defenseStats.blockedKickReturns) || 0) * 2;
      const pa = parseFloat(defenseStats.pointsAllowed) || 0;
      if (pa === 0) total += 12;
      else if (pa <= 6) total += 8;
      else if (pa <= 10) total += 5;
    }

    return Math.round(total * 10) / 10;
  };

  const calculatedPoints = calculatePoints();

  const inputClass = "w-full px-2 py-1 text-sm bg-white border border-gray-300 rounded focus:ring-1 focus:ring-blue-500";

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900">{position} Calculator</h4>
        <div className="text-lg font-bold text-blue-600">{calculatedPoints.toFixed(1)} pts</div>
      </div>

      {isHockeySkater && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Goals (6 pts)</label>
            <input
              type="number"
              value={skaterStats.goals}
              onChange={(e) => setSkaterStats({ ...skaterStats, goals: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Assists (4 pts)</label>
            <input
              type="number"
              value={skaterStats.assists}
              onChange={(e) => setSkaterStats({ ...skaterStats, assists: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">+/- (2 pts)</label>
            <input
              type="number"
              value={skaterStats.plusMinus}
              onChange={(e) => setSkaterStats({ ...skaterStats, plusMinus: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">PP Points (2 pts)</label>
            <input
              type="number"
              value={skaterStats.powerPlayPoints}
              onChange={(e) => setSkaterStats({ ...skaterStats, powerPlayPoints: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Shots on Goal (0.9 pts)</label>
            <input
              type="number"
              value={skaterStats.shotsOnGoal}
              onChange={(e) => setSkaterStats({ ...skaterStats, shotsOnGoal: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Blocked Shots (1 pt)</label>
            <input
              type="number"
              value={skaterStats.blockedShots}
              onChange={(e) => setSkaterStats({ ...skaterStats, blockedShots: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {isGoalie && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Win (5 pts)</label>
            <select
              value={goalieStats.win}
              onChange={(e) => setGoalieStats({ ...goalieStats, win: e.target.value })}
              style={{ color: '#111827' }}
              className={inputClass}
            >
              <option value="">No</option>
              <option value="1">Yes (1)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Shutout (5 pts)</label>
            <select
              value={goalieStats.shutout}
              onChange={(e) => setGoalieStats({ ...goalieStats, shutout: e.target.value })}
              style={{ color: '#111827' }}
              className={inputClass}
            >
              <option value="">No</option>
              <option value="1">Yes (1)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Goals Against (-3 pts ea)</label>
            <input
              type="number"
              value={goalieStats.goalsAgainst}
              onChange={(e) => setGoalieStats({ ...goalieStats, goalsAgainst: e.target.value })}
              placeholder="0"
              min="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Saves (0.6 pts ea)</label>
            <input
              type="number"
              value={goalieStats.saves}
              onChange={(e) => setGoalieStats({ ...goalieStats, saves: e.target.value })}
              placeholder="0"
              min="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {isOffensive && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          {position === 'QB' && (
            <>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Pass Yds</label>
                <input
                  type="number"
                  value={offensiveStats.passingYards}
                  onChange={(e) => setOffensiveStats({ ...offensiveStats, passingYards: e.target.value })}
                  placeholder="0"
                  style={{ color: '#111827' }}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Pass TDs</label>
                <input
                  type="number"
                  value={offensiveStats.passingTDs}
                  onChange={(e) => setOffensiveStats({ ...offensiveStats, passingTDs: e.target.value })}
                  placeholder="0"
                  style={{ color: '#111827' }}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">INTs</label>
                <input
                  type="number"
                  value={offensiveStats.interceptions}
                  onChange={(e) => setOffensiveStats({ ...offensiveStats, interceptions: e.target.value })}
                  placeholder="0"
                  style={{ color: '#111827' }}
                  className={inputClass}
                />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Rush Yds</label>
            <input
              type="number"
              value={offensiveStats.rushingYards}
              onChange={(e) => setOffensiveStats({ ...offensiveStats, rushingYards: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Rush TDs</label>
            <input
              type="number"
              value={offensiveStats.rushingTDs}
              onChange={(e) => setOffensiveStats({ ...offensiveStats, rushingTDs: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          {['RB', 'WR', 'TE', 'FLEX'].includes(position) && (
            <>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Rec Yds</label>
                <input
                  type="number"
                  value={offensiveStats.receivingYards}
                  onChange={(e) => setOffensiveStats({ ...offensiveStats, receivingYards: e.target.value })}
                  placeholder="0"
                  style={{ color: '#111827' }}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Rec TDs</label>
                <input
                  type="number"
                  value={offensiveStats.receivingTDs}
                  onChange={(e) => setOffensiveStats({ ...offensiveStats, receivingTDs: e.target.value })}
                  placeholder="0"
                  style={{ color: '#111827' }}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Receptions</label>
                <input
                  type="number"
                  value={offensiveStats.receptions}
                  onChange={(e) => setOffensiveStats({ ...offensiveStats, receptions: e.target.value })}
                  placeholder="0"
                  style={{ color: '#111827' }}
                  className={inputClass}
                />
              </div>
            </>
          )}
          <div>
            <label className="block text-xs text-gray-600 mb-1">2PT Conv</label>
            <input
              type="number"
              value={offensiveStats.twoPointConversions}
              onChange={(e) => setOffensiveStats({ ...offensiveStats, twoPointConversions: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Fumbles Lost</label>
            <input
              type="number"
              value={offensiveStats.fumblesLost}
              onChange={(e) => setOffensiveStats({ ...offensiveStats, fumblesLost: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {isKicker && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">FG 1-30 yds</label>
            <input
              type="number"
              value={kickingStats.fieldGoals30}
              onChange={(e) => setKickingStats({ ...kickingStats, fieldGoals30: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">FG 31-40 yds</label>
            <input
              type="number"
              value={kickingStats.fieldGoals40}
              onChange={(e) => setKickingStats({ ...kickingStats, fieldGoals40: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">FG 41-50 yds</label>
            <input
              type="number"
              value={kickingStats.fieldGoals50}
              onChange={(e) => setKickingStats({ ...kickingStats, fieldGoals50: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">FG 50+ yds</label>
            <input
              type="number"
              value={kickingStats.fieldGoals50Plus}
              onChange={(e) => setKickingStats({ ...kickingStats, fieldGoals50Plus: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Extra Points</label>
            <input
              type="number"
              value={kickingStats.extraPoints}
              onChange={(e) => setKickingStats({ ...kickingStats, extraPoints: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {isDefense && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Sacks</label>
            <input
              type="number"
              value={defenseStats.sacks}
              onChange={(e) => setDefenseStats({ ...defenseStats, sacks: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Turnovers</label>
            <input
              type="number"
              value={defenseStats.turnovers}
              onChange={(e) => setDefenseStats({ ...defenseStats, turnovers: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Touchdowns</label>
            <input
              type="number"
              value={defenseStats.touchdowns}
              onChange={(e) => setDefenseStats({ ...defenseStats, touchdowns: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Safeties</label>
            <input
              type="number"
              value={defenseStats.safeties}
              onChange={(e) => setDefenseStats({ ...defenseStats, safeties: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Blocked Kick Returns</label>
            <input
              type="number"
              value={defenseStats.blockedKickReturns}
              onChange={(e) => setDefenseStats({ ...defenseStats, blockedKickReturns: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Points Allowed</label>
            <input
              type="number"
              value={defenseStats.pointsAllowed}
              onChange={(e) => setDefenseStats({ ...defenseStats, pointsAllowed: e.target.value })}
              placeholder="0"
              style={{ color: '#111827' }}
              className={inputClass}
            />
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => onApplyPoints(calculatedPoints)}
        className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
      >
        Use {calculatedPoints.toFixed(1)} Points
      </button>
    </div>
  );
}

function ResultEntryModal({
  event,
  players,
  entries,
  onClose,
  onSuccess,
}: {
  event: Event;
  players: Player[];
  entries: Entry[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    team1Score: event.homeScore?.toString() || '',
    team2Score: event.awayScore?.toString() || '',
  });
  const [activeCalculator, setActiveCalculator] = useState<string | null>(null);

  const pickedPlayerIds = new Set<string>();
  entries.forEach(entry => {
    if (isRosterSelectionPicks(entry.picks) && entry.picks.positions) {
      Object.values(entry.picks.positions).forEach((pick: any) => {
        if (pick.playerId) {
          pickedPlayerIds.add(pick.playerId);
        }
      });
    }
  });

  const showAllPlayers = pickedPlayerIds.size === 0;

  const relevantPlayers = players.filter(p => {
    // For pools with homeTeam/awayTeam events, filter by the two competing teams
    const hasEventTeams = event.homeTeam && event.awayTeam;
    const isFromEventTeams = hasEventTeams
      ? (event.homeTeam?.id === p.team.id || event.awayTeam?.id === p.team.id)
      : true; // If no event teams set, don't filter by team

    if (showAllPlayers) {
      return isFromEventTeams;
    }
    // When entries exist, only show picked players
    // For events with teams set, also require the player is from one of the event teams
    return pickedPlayerIds.has(p.id) && isFromEventTeams;
  });

  const [playerStats, setPlayerStats] = useState<{ [playerId: string]: string }>(() => {
    const stats: { [playerId: string]: string } = {};
    
    // Initialize all players with empty strings
    relevantPlayers.forEach(player => {
      stats[player.id] = '';
    });
    
    return stats;
  });

  const handlePlayerPointsChange = (playerId: string, value: string) => {
    setPlayerStats(prev => ({ ...prev, [playerId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      
      const requestData: EnterResultRequest = {
        team1Score: formData.team1Score ? parseInt(formData.team1Score) : undefined,
        team2Score: formData.team2Score ? parseInt(formData.team2Score) : undefined,
        playerStats: Object.entries(playerStats)
          .filter(([_, points]) => points && parseFloat(points) !== 0)
          .map(([playerId, points]) => ({
            playerId,
            points: parseFloat(points),
          })),
      };

      await api.enterResult(event.id, requestData, token);
      onSuccess();
    } catch (err) {
      console.error('Failed to enter result:', err);
      setError(err instanceof Error ? err.message : 'Failed to enter result');
    } finally {
      setLoading(false);
    }
  };

  const playersGroupedByTeam = relevantPlayers.reduce((acc, player) => {
    const teamName = player.team.name || player.team.abbreviation || 'Unknown Team';
    if (!acc[teamName]) {
      acc[teamName] = [];
    }
    acc[teamName].push(player);
    return acc;
  }, {} as { [teamName: string]: Player[] });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">Enter Result: {event.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {event.homeTeam && event.awayTeam && (
            <div className="mb-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Game Score</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {event.homeTeam.shortName} Score
                  </label>
                  <input
                    type="number"
                    value={formData.team1Score}
                    onChange={(e) => setFormData({ ...formData, team1Score: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{ color: '#111827' }}
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {event.awayTeam.shortName} Score
                  </label>
                  <input
                    type="number"
                    value={formData.team2Score}
                    onChange={(e) => setFormData({ ...formData, team2Score: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{ color: '#111827' }}
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Player Fantasy Points</h3>
            <p className="text-xs text-gray-500 mb-3">
              {showAllPlayers 
                ? 'No entries submitted yet - showing all players from these teams.'
                : 'Only players selected in entries are shown.'} Enter points or use calculator. Leave blank for 0 points.
            </p>
            
            {relevantPlayers.length === 0 ? (
              <div className="bg-gray-50 p-8 rounded-lg text-center">
                <p className="text-gray-500">
                  {showAllPlayers 
                    ? 'No players found from these teams. Import players first.'
                    : 'No players from these teams were selected in any entries.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(playersGroupedByTeam).map(([teamName, teamPlayers]) => (
                  <div key={teamName} className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">{teamName}</h4>
                    <div className="space-y-3">
                      {teamPlayers.map((player) => (
                        <div key={player.id}>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <label className="block text-sm text-gray-700">
                                <span className="font-medium">{player.name}</span>
                                <span className="text-gray-500 ml-1">({player.position})</span>
                              </label>
                            </div>
                            <input
                              type="number"
                              step="0.1"
                              value={playerStats[player.id] || ''}
                              onChange={(e) => handlePlayerPointsChange(player.id, e.target.value)}
                              placeholder="0"
                              className="w-24 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right"
                              style={{ color: '#111827' }}
                            />
                            <button
                              type="button"
                              onClick={() => setActiveCalculator(activeCalculator === player.id ? null : player.id)}
                              className="px-3 py-2 text-sm bg-white border border-gray-300 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              🧮
                            </button>
                          </div>
                          
                          {activeCalculator === player.id && (
                            <div className="mt-2">
                              <ScoringCalculator
                                position={player.position}
                                onApplyPoints={(points) => {
                                  handlePlayerPointsChange(player.id, points.toString());
                                  setActiveCalculator(null);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Submitting...' : 'Submit Result'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
